/**
 * Form Field Component
 * 
 * Reusable form field with validation display
 * - Label and input
 * - Error message display
 * - Invalid field highlighting
 * - Accessible with ARIA attributes
 * 
 * Requirements: 12.4, 13.4
 */

import React from 'react';

interface FormFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'date' | 'datetime-local' | 'number' | 'textarea' | 'select';
  value: string | number;
  error?: string;
  touched?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  options?: Array<{ value: string; label: string }>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur?: () => void;
  className?: string;
  helpText?: string;
}

/**
 * Form Field Component
 * 
 * Displays a form field with label, input, and error message
 * Requirements: 12.4, 13.4
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  error,
  touched,
  required,
  placeholder,
  disabled,
  options,
  onChange,
  onBlur,
  className = '',
  helpText,
}) => {
  const hasError = touched && error;
  const inputId = `field-${name}`;
  const errorId = `error-${name}`;
  const helpId = `help-${name}`;

  const baseInputClasses = `
    w-full px-3 py-2 border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    disabled:bg-gray-100 disabled:cursor-not-allowed
    transition-colors
  `;

  const inputClasses = hasError
    ? `${baseInputClasses} border-red-500 focus:ring-red-500 focus:border-red-500`
    : `${baseInputClasses} border-gray-300`;

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`${inputClasses} min-h-[100px]`}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? errorId : helpText ? helpId : undefined}
          aria-required={required}
        />
      );
    }

    if (type === 'select' && options) {
      return (
        <select
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          className={inputClasses}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? errorId : helpText ? helpId : undefined}
          aria-required={required}
        >
          <option value="">Select {label}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={inputClasses}
        aria-invalid={hasError ? 'true' : 'false'}
        aria-describedby={hasError ? errorId : helpText ? helpId : undefined}
        aria-required={required}
      />
    );
  };

  return (
    <div className={`mb-4 ${className}`}>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
        {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
      </label>
      
      {renderInput()}
      
      {helpText && !hasError && (
        <p id={helpId} className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}
      
      {hasError && (
        <p
          id={errorId}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
